/*
 * A THREAD YOU HAVE READ BEFORE SHOULD NOT MAKE YOU WAIT FOR IT AGAIN.
 *
 * Opening a conversation already paints immediately — the object is live before its history lands
 * and the view draws a skeleton — but the words themselves waited on a round trip to the archive
 * every single time, including for the thread you closed ninety seconds ago. On anything but a local
 * connection that is the visible stall on the one interaction people make most, and it reads as the
 * app being slow rather than as data loading.
 *
 * So: paint from cache, reconcile from the archive. The same shape the app's structural snapshots
 * use, over the same bounded store, and the ordering is what makes it safe — `hydrate` REPLACES and
 * refuses once a thread is live, so a cached transcript can only ever be displaced by the real one,
 * never the other way round.
 *
 * WHAT IS CACHED IS THE FOLD, not the raw rows. Folding is where "which turn owns this tool call"
 * is decided, it is the same function both transcript surfaces read through, and doing it once on
 * write rather than once per open is the difference between a cache that saves a round trip and one
 * that saves a round trip and a parse.
 *
 * KEYED BY USER as well as by conversation. Two accounts on one browser must not read each other's
 * threads out of a shared store, and a cache is the easiest place in an app to forget that.
 */
import { IdbStore } from '../cache/idb-store';
/**
 * The budget.
 *
 * A transcript is text and is small — a long thread with tool payloads runs to a few hundred KB —
 * so 16 MB is on the order of a hundred threads, comfortably more than anyone revisits, and a small
 * fraction of what a browser grants an origin. The LRU is what decides which hundred: threads are
 * revisited in a strong recency pattern, which is exactly the access shape an LRU is for.
 */
const MAX_BYTES = 16 * 1024 * 1024;
/**
 * How long a cached transcript may be trusted.
 *
 * Long, because staleness here is nearly free: the archive read is already in flight when the cache
 * is painted, so a stale entry is visible for the length of one request and is then replaced. The
 * limit exists to stop the store holding threads nobody will open again, not to protect the reader
 * from old data.
 */
const MAX_AGE_MS = 14 * 24 * 3600 * 1000;
const store = new IdbStore({ db: 'vitesse-ai-transcripts', maxBytes: MAX_BYTES, maxAgeMs: MAX_AGE_MS });
const key = (userId, conversationId) => `${userId}/${conversationId}`;
/** The turns of a thread as they were last read, or nothing. Never throws: the caller's fallback is
 *  the archive, which is what it did before this existed. */
export async function cachedTranscript(userId, conversationId) {
    if (!userId || !conversationId)
        return null;
    return store.get(key(userId, conversationId));
}
/**
 * Remember a thread as it currently reads.
 *
 * Written after a hydrate rather than after every turn: a live thread is already on screen, and
 * writing on each message would spend a storage write per token-stream completion to cache something
 * nobody is going to reload in the next second. The next open is what this is for.
 *
 * An EMPTY transcript is not written. A thread with no turns caches nothing worth painting, and
 * writing it would let a failed read overwrite a good entry with a blank one — the cache's one
 * genuinely harmful failure mode, since it would then paint emptiness instantly and confidently.
 */
export function rememberTranscript(userId, conversationId, value) {
    if (!userId || !conversationId || !value.turns.length)
        return;
    store.put(key(userId, conversationId), value);
}
/** Forget one thread — after a delete, so a deleted conversation cannot be painted back from cache
 *  on the next open. */
export function forgetTranscript(userId, conversationId) {
    if (!userId || !conversationId)
        return;
    void store.delete([key(userId, conversationId)]);
}
/** Drop every thread belonging to anyone but this user. The sign-out/sign-in sweep: keys are
 *  user-prefixed, so nothing has to be remembered at logout for the next account to be safe. */
export function sweepTranscripts(userId) {
    if (!userId)
        return;
    void store.sweep([`${userId}/`]);
}
//# sourceMappingURL=transcript-cache.js.map